/**
 * First-touch attribution for the free audit.
 *
 * Captured from the first page a visitor views in this tab session (not only
 * /audit), so it survives navigating to /audit through the header or footer.
 * The keys match the backend intake whitelist; values are capped at 256
 * characters. Storage can be disabled (privacy modes), so every access is
 * guarded and the current page is used as a fallback.
 */
export const FIRST_TOUCH_KEY = "topgeo_first_touch_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;
const TOUCH_KEYS = [...UTM_KEYS, "referrer_host", "landing_path"] as const;
const MAX_VALUE_LENGTH = 256;

export type FirstTouch = Partial<Record<(typeof TOUCH_KEYS)[number], string>>;

function clip(value: string | null | undefined): string | undefined {
  const trimmed = (value ?? "").trim();
  return trimmed ? trimmed.slice(0, MAX_VALUE_LENGTH) : undefined;
}

function currentTouch(): FirstTouch {
  const touch: FirstTouch = {};
  const params = new URLSearchParams(window.location.search);
  for (const key of UTM_KEYS) {
    const value = clip(params.get(key));
    if (value) touch[key] = value;
  }
  try {
    const referrer = new URL(document.referrer);
    // An internal referrer says nothing about how the visitor found us.
    if (referrer.hostname && referrer.host !== window.location.host) {
      touch.referrer_host = clip(referrer.hostname);
    }
  } catch {
    /* No referrer, or not a URL. */
  }
  const path = clip(window.location.pathname);
  if (path) touch.landing_path = path;
  return touch;
}

/** Record the first page of the session; later pages never overwrite it. */
export function captureFirstTouch(): void {
  try {
    if (window.sessionStorage.getItem(FIRST_TOUCH_KEY) !== null) return;
    window.sessionStorage.setItem(
      FIRST_TOUCH_KEY,
      JSON.stringify(currentTouch()),
    );
  } catch {
    /* Storage blockers fail open. */
  }
}

/** The stored first touch, or the current page when nothing was stored. */
export function readFirstTouch(): FirstTouch {
  try {
    const raw = window.sessionStorage.getItem(FIRST_TOUCH_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as Record<string, unknown>;
      const touch: FirstTouch = {};
      for (const key of TOUCH_KEYS) {
        const value = stored[key];
        if (typeof value === "string" && clip(value))
          touch[key] = clip(value);
      }
      return touch;
    }
  } catch {
    /* Fall through to the current page. */
  }
  return currentTouch();
}
